/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/coldefs/metadata/molecule/pubchem
 * @desc Column Definition for metadata molecule pubchem fields
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { ICONS } from 'labstep-web/constants/icons';
import type {
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import type { colDefPubchemFieldType } from 'labstep-web/grid/Index/coldefs/Molecule/pubchem/types';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolValue/components/ColumnHeader';
import { GridSmartTableProtocolValueService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value.service';
import type { Entity } from 'labstep-web/models/entity.model';
import type { Experiment } from 'labstep-web/models/experiment.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { Resource } from 'labstep-web/models/resource.model';
import { PubchemService } from 'labstep-web/services/pubchem.service';

export const getMetadataMoleculePubchemColDef = (
  experimentOrProtocol: Experiment | Protocol,
  parentEntityClass: string,
  metadataType: MetadataType,
  metadataLabel: string,
  fieldName: string,
  field: colDefPubchemFieldType,
  protocolValueName?: string,
): SmartTableColDef => {
  return {
    colId: protocolValueName
      ? `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}:${field.name}:${protocolValueName}:`
      : `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}:${field.name}`,
    sortable: true,
    headerName: fieldName,
    headerComponent: GridSmartTableColumnHeader,
    headerComponentParams: (params: HeaderComponentParamsProps) => {
      return {
        icon: protocolValueName
          ? ICONS.metadata.secondary
          : ICONS.metadata.primary,
        cornerIcon:
          protocolValueName &&
          (parentEntityClass === Resource.entityName
            ? ICONS.resource.primary
            : ICONS.resource_item.primary),
        secondaryHeader:
          protocolValueName &&
          `${protocolValueName}${GridSmartTableProtocolValueService.getMetadataParentPrefix(parentEntityClass)}`,
        toolPanelColumnName: `${GridSmartTableProtocolValueService.getMetadataParentPrefix(parentEntityClass)}${metadataLabel} - ${fieldName}`,
        experimentOrProtocol,
      };
    },
    editable: (params: EditableCallbackParams) =>
      GridSmartTableProtocolValueService.getMetadataEditable(
        params,
        experimentOrProtocol,
        metadataType,
        metadataLabel,
        parentEntityClass,
        protocolValueName,
      ),
    valueGetter: (
      params: ValueGetterParams,
    ): string | number | null => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (!metadata || !metadata.molecule) {
        return '';
      }
      return PubchemService.getPubchemFieldValue(
        metadata.molecule,
        field,
      );
    },
    valueSetter: (params: ValueSetterParams): boolean => {
      if (!params.node) {
        return false;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (!metadata || !metadata.molecule) {
        return false;
      }
      const molecule = metadata.molecule;

      return GridSmartTableProtocolValueService.updateEntity(
        params,
        molecule.entityName,
        molecule.guid,
        {
          pubchem: PubchemService.getPubchemBody(
            molecule,
            field,
            params.newValue,
          ),
        },
      );
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      if (!GridSmartTableProtocolValueService.isEditable(params)) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (!metadata) {
        return (
          <TextPlaceholder
            onClick={() => {
              return GridSmartTableProtocolValueService.createMetadata(
                params,
                experimentOrProtocol,
                parentEntityClass,
                metadataType,
                metadataLabel,
                protocolValueName,
                {
                  molecule: { name: 'Untitled structure' },
                },
                () => {
                  GridSmartTableProtocolValueService.startEditingCell(
                    params,
                  );
                },
              );
            }}
          >
            Create structure
          </TextPlaceholder>
        );
      }
      return metadata.molecule
        ? PubchemService.getPubchemFieldValue(
            metadata.molecule,
            field,
          )
        : null;
    },
    cellStyle: (params) =>
      GridSmartTableProtocolValueService.getCellStyle(
        params,
        experimentOrProtocol,
        Metadata.entityName,
        metadataLabel,
        parentEntityClass,
      ),
  };
};
